/**
 * Buyer <-> vendor chat (shop.threads + shop.messages).
 *
 * Per-user threads. RLS allows:
 *   * buyer (auth.uid() = thread.buyer_user_id) to read/write
 *   * vendor team (shop.vendor_users) to read/write
 *   * platform admin to read all
 *
 * Email side-effects (new-message + 24h unread reminder) are handled by:
 *   * shop.fn_messages_on_insert trigger -- creates shop.message_notifications.
 *   * shop-message-send Edge Function -- dispatches initial email.
 *   * shop-unread-reminder-cron Edge Function -- dispatches reminders.
 */

import { supabase } from '../client';
import { fireShopAdConversion } from './shopAdsV2';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';
import type {
  ShopMessage,
  ShopMessageAttachment,
  ShopMessageSenderKind,
  ShopThread,
} from './shopTypes';

// =========================================================================
// Threads
// =========================================================================

export interface ListShopThreadsInput extends KeysetInput {
  /** Buyer-side: restrict to threads owned by this org. */
  buyerOrgId?: string;
  /** Vendor-side: restrict to threads with this vendor. */
  vendorId?: string;
  /** When true, only threads with at least one unread message for the caller. */
  unreadOnly?: boolean;
  side: 'buyer' | 'vendor';
}

export async function listShopThreads(
  input: ListShopThreadsInput,
): Promise<ListResult<ShopThread>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('threads')
    .select('*', selectCountOption(input.countMode))
    .is('archived_at', null);

  if (input.buyerOrgId) q = q.eq('buyer_org_id', input.buyerOrgId);
  if (input.vendorId) q = q.eq('vendor_id', input.vendorId);
  if (input.unreadOnly) {
    q = q.gt(input.side === 'buyer' ? 'buyer_unread_count' : 'vendor_unread_count', 0);
  }

  q = applyKeyset(q, input.cursor, 'last_message_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopThread[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'last_message_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

/**
 * Vendor-side inbox listing with enriched buyer labels.
 *
 * Wraps `shop.rpc_list_vendor_threads_with_buyer` (security-definer RPC
 * installed by 20260525002800_shop_vendor_thread_buyer_labels.sql). The
 * RPC joins shop.threads to core.organizations and core.users so the
 * sell-app inbox can show the actual buyer org name + user name/email,
 * which the bare PostgREST .from('threads') chain cannot do (vendors
 * lack direct read on cross-org core.organizations rows).
 *
 * Keyset pagination matches `listShopThreads`: `KeysetCursor.sortValue`
 * is the last row's `last_message_at`, `id` is its UUID, ordered DESC.
 */
export interface ShopVendorThreadWithBuyer extends ShopThread {
  buyer_org_name: string | null;
  buyer_org_slug: string | null;
  buyer_user_display_name: string | null;
  buyer_user_email: string | null;
}

export interface ListShopVendorThreadsWithBuyerInput extends KeysetInput {
  vendorId: string;
  unreadOnly?: boolean;
}

export async function listShopVendorThreadsWithBuyer(
  input: ListShopVendorThreadsWithBuyerInput,
): Promise<ListResult<ShopVendorThreadWithBuyer>> {
  const pageSize = clampPageSize(input.pageSize);
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_list_vendor_threads_with_buyer', {
      p_vendor_id: input.vendorId,
      p_unread_only: input.unreadOnly ?? false,
      p_cursor_last_message_at: input.cursor?.sortValue ?? null,
      p_cursor_id: input.cursor?.id ?? null,
      p_limit: pageSize,
    });
  if (error) throw error;
  const rows = (data ?? []) as ShopVendorThreadWithBuyer[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'last_message_at',
    ),
    total: null,
    totalIsExact: false,
  };
}

export async function getShopThread(id: string): Promise<ShopThread | null> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('threads')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as ShopThread | null;
}

export interface OpenShopThreadInput {
  vendorId: string;
  buyerOrgId: string;
  subject?: string;
}

/** Open (or fetch existing) thread between the calling buyer and vendor. */
export async function openShopThread(
  input: OpenShopThreadInput,
): Promise<ShopThread> {
  const { data: userData, error: ue } = await supabase().auth.getUser();
  if (ue) throw ue;
  const userId = userData.user?.id;
  if (!userId) throw new Error('Sign in to open a thread.');

  // Look up the existing GENERAL (non-PO, non-order) thread. Since
  // 20260525004400_shop_threads_order_id.sql, shop.threads can hold
  // multiple non-archived rows per (vendor, buyer) -- one general
  // thread plus zero or more PO/order-scoped threads. The vendor-
  // profile "Message" button always opens the general thread, so we
  // must filter on po_id IS NULL AND order_id IS NULL or the
  // .maybeSingle() will trip "JSON object requested, multiple (or no)
  // rows returned" for buyers who already have an order thread.
  const { data: existing, error: le } = await (supabase() as any)
    .schema('shop')
    .from('threads')
    .select('*')
    .eq('vendor_id', input.vendorId)
    .eq('buyer_user_id', userId)
    .is('po_id', null)
    .is('order_id', null)
    .is('archived_at', null)
    .maybeSingle();
  if (le) throw le;
  if (existing) return existing as ShopThread;

  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('threads')
    .insert({
      vendor_id: input.vendorId,
      buyer_user_id: userId,
      buyer_org_id: input.buyerOrgId,
      subject: input.subject ?? null,
    })
    .select('*')
    .single();
  if (error) throw error;
  // Newly-opened thread; fire conversion (no-op when last-click absent).
  void fireShopAdConversion({
    eventType: 'conversion_message_initiated',
    targetKind: 'message_thread',
    targetId: (data as ShopThread).id,
  });
  return data as ShopThread;
}

export async function markShopThreadRead(threadId: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_mark_thread_read', { p_thread_id: threadId });
  if (error) throw error;
}

export interface OpenShopThreadFromProductInput {
  vendorId: string;
  buyerOrgId: string;
  /** Product fields, snapshotted onto the message attachment. */
  product: {
    id: string;
    vendor_slug: string;
    slug: string;
    name: string;
    image_url: string | null;
    price_cents: number;
    currency: string;
  };
  /**
   * Optional first-message body. Defaults to a neutral "I'd like more
   * information about {product.name}." that the operator can edit.
   */
  bodyMd?: string;
}

/**
 * Open (or fetch existing) general thread between caller and vendor,
 * then post a first message with the product attached as a card.
 *
 * Used by the "Message about this product" CTA on product detail
 * pages -- so the vendor opens the thread and immediately sees which
 * product started it.
 *
 * If a general thread already exists, the message is appended (we do
 * NOT clobber an existing conversation). Callers should still expect
 * the same return shape (`{ thread, message }`).
 */
export async function openShopThreadFromProduct(
  input: OpenShopThreadFromProductInput,
): Promise<{ thread: ShopThread; message: ShopMessage }> {
  const thread = await openShopThread({
    vendorId: input.vendorId,
    buyerOrgId: input.buyerOrgId,
  });
  const body =
    input.bodyMd?.trim() ||
    `I'd like more information about ${input.product.name}.`;
  const attachment: ShopMessageAttachment = {
    kind: 'product',
    product_id: input.product.id,
    vendor_id: input.vendorId,
    vendor_slug: input.product.vendor_slug,
    product_slug: input.product.slug,
    product_name_snapshot: input.product.name,
    image_url_snapshot: input.product.image_url,
    price_cents_snapshot: input.product.price_cents,
    currency_snapshot: input.product.currency,
  };
  const message = await sendShopMessage({
    threadId: thread.id,
    senderKind: 'buyer',
    bodyMd: body,
    attachments: [attachment],
  });
  return { thread, message };
}

// =========================================================================
// Messages
// =========================================================================

export interface ListShopMessagesInput extends KeysetInput {
  threadId: string;
  /** Default ascending (oldest first). Set false for newest first. */
  ascending?: boolean;
}

export async function listShopMessages(
  input: ListShopMessagesInput,
): Promise<ListResult<ShopMessage>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('messages')
    .select('*', selectCountOption(input.countMode))
    .eq('thread_id', input.threadId);

  const ascending = input.ascending !== false;
  q = q.order('created_at', { ascending }).order('id', { ascending });
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopMessage[];
  // We don't expose a keyset cursor for messages (the per-thread volume
  // is small; the inbox UI scrolls a virtualized window and lazy-loads
  // older history by passing the oldest message's `created_at` back in).
  return {
    rows,
    nextCursor: null,
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

export interface SendShopMessageInput {
  threadId: string;
  /** 'buyer' or 'vendor' -- caller must own the role. RLS verifies. */
  senderKind: ShopMessageSenderKind;
  bodyMd: string;
  /**
   * Optional inline attachments. Use {@link ShopMessageAttachment}
   * shapes -- storage-URL attachments (`image` / `pdf` / `other`) and
   * product cards (`product`) are both supported.
   */
  attachments?: ShopMessageAttachment[];
}

/**
 * Direct insert via PostgREST. The post-insert trigger creates the
 * shop.message_notifications row(s); the calling client should also
 * invoke the `shop-message-send` Edge Function (one-shot, fire and
 * forget) to dispatch the email immediately. The cron picks up any
 * notifications that the live dispatch missed.
 */
export async function sendShopMessage(input: SendShopMessageInput): Promise<ShopMessage> {
  const { data: userData, error: ue } = await supabase().auth.getUser();
  if (ue) throw ue;
  const userId = userData.user?.id;
  if (!userId) throw new Error('Sign in to send a message.');

  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('messages')
    .insert({
      thread_id: input.threadId,
      sender_kind: input.senderKind,
      sender_user_id: userId,
      body_md: input.bodyMd,
      attachments: input.attachments ?? [],
    })
    .select('*')
    .single();
  if (error) throw error;

  // Fire-and-forget initial email dispatch via the Edge Function. If it
  // fails the cron will still pick the notification up after some delay.
  void invokeShopMessageSend(data.id as string).catch(() => {});

  return data as ShopMessage;
}

/**
 * Invokes the shop-message-send Edge Function for a single message id.
 * Idempotent on the server: if the notification has already been sent,
 * the function no-ops.
 */
async function invokeShopMessageSend(messageId: string): Promise<void> {
  const { data: sess } = await supabase().auth.getSession();
  if (!sess.session?.access_token) return;
  try {
    const { error } = await supabase().functions.invoke('shop-message-send', {
      body: { message_id: messageId },
    });
    if (error) {
      console.warn('[shop] shop-message-send invoke failed:', error.message);
    }
  } catch (err) {
    console.warn('[shop] shop-message-send invoke error:', err);
  }
}
